/**
 * "Export as HTML…" dialog (S15, Pro) — the ONE option is "Include queries"
 * (SQL text only; connection ids/names + database names are never embedded,
 * with or without it ticked — see `services/htmlSnapshot.ts`'s security
 * doc). Confirming hands off to `useDashboardExportFlow.confirmExportHtml`.
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

export function HtmlSnapshotExportDialog({
  open,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (includeQueries: boolean) => void;
}) {
  const [includeQueries, setIncludeQueries] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setIncludeQueries(false);
        onOpenChange(next);
      }}
    >
      <DialogContent data-testid="html-snapshot-export-dialog">
        <DialogHeader>
          <DialogTitle>Export as HTML…</DialogTitle>
          <DialogDescription>
            Creates a self-contained, offline HTML file with each tile's current data baked in —
            hover chart tooltips, local table sorting, and a light/dark toggle all work without a
            connection. This file contains your dashboard's query results.
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={includeQueries}
            data-testid="html-snapshot-include-queries"
            onCheckedChange={(checked) => setIncludeQueries(checked === true)}
          />
          Include queries (SQL text only — connection and database names are never included)
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="html-snapshot-cancel">
            Cancel
          </Button>
          <Button onClick={() => onConfirm(includeQueries)} data-testid="html-snapshot-confirm">
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
